<template>
    <tailwind-layout title="Tenant Screening">
        <template #header>
            <div class="px-4 sm:px-6 lg:px-8 mx-auto mt-1 hidden lg:flex justify-between items-center">
                <!-- Block for lg and larger -->
                <h1 class="font-semibold text-2xl leading-tight">My Rental Applications</h1>
            </div>
            <div class="mb-3 px-6 lg:px-8 mx-auto flex flex-col lg:hidden"><!-- Block for sm, md, lg (buttons will stack on smaller screens) -->
                <div class="flex justify-between items-center">
                    <h1 class="font-semibold text-2xl leading-tight">My Rental Applications</h1>
                </div>
            </div>
        </template>
        <div class="">
            <!-- 86bc1ymu6: an account created for a self credit check has no
                 applications at all, so an empty table reads as broken. Say so
                 plainly instead. -->
            <div v-if="!applications.length" class="flex flex-col items-center justify-center text-center py-16 px-6">
                <h2 class="text-xl font-bold text-gray-900">You don't have any active rental applications.</h2>
                <p class="mt-2 max-w-xl text-sm text-gray-600">
                    Applications start from a property-specific link. If you are applying for a rental, ask your agent
                    or landlord for the RentRisk link to that property.
                </p>
            </div>

            <div v-else class="rounded-md lg:shadow-lg shadow-none overflow-x-auto">
                <div class="flex flex-col">
                    <div class="overflow-x-auto">
                        <div class="align-middle inline-block min-w-full">
                            <div class="shadow-lg lg:border">
                                <table class="min-w-full divide-y divide-gray-300 hidden lg:table">
                                    <thead class="bg-blue-50">
                                        <tr>
                                            <th scope="col"
                                                class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                                Property Address
                                            </th>
                                            <th scope="col"
                                                class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                                My Progress
                                            </th>
                                            <th scope="col"
                                                class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                                Date Started
                                            </th>
                                            <th scope="col"
                                                class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                                Report Expire Date
                                            </th>
                                            <th scope="col"
                                                class="px-3 py-3.5 text-sm font-semibold text-gray-900">
                                                Report
                                            </th>
                                            <th scope="col"
                                                class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">

                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody class="divide-y divide-gray-200">
                                        <tr v-for="(application, index) in applications" :key="index"
                                            :class="index % 2 === 0 ? 'bg-gray-50 border-b hover:bg-yellow-50 hover:cursor-pointer' : 'bg-white border-b hover:bg-yellow-50 hover:cursor-pointer'"
                                            @click="openApplication(application.id)"
                                            >
                                            <td
                                                class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                                <font-awesome-icon v-if="!application.is_complete" :icon="faExclamationTriangle" class="h-5 w-5 text-red-500" aria-hidden="true" />
                                                {{ application.property_address }}
                                            </td>
                                            <td
                                                class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                                <div v-if="application.is_complete" class="flex items-center">
                                                    <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                        Complete
                                                    </span>
                                                </div>
                                                <div v-else class="flex items-center space-x-2">
                                                    <div class="relative group flex-1">
                                                        <div class="bg-gray-200 rounded-full h-2 cursor-pointer">
                                                            <div 
                                                                class="bg-blue-600 h-2 rounded-full transition-all duration-300 ease-in-out"
                                                                :style="{ width: application.completion_percentage + '%' }"
                                                            ></div>
                                                        </div>
                                                        <!-- Tooltip -->
                                                        <div class="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block z-20 pointer-events-none">
                                                            <div class="bg-gray-900 text-white text-xs rounded-lg py-2 px-3 whitespace-nowrap shadow-lg">
                                                                {{ application.steps_completed }}/{{ application.total_steps }} ({{ application.current_step_display_name || 'Getting Started' }})
                                                                <div class="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <span class="text-xs text-gray-600 font-medium min-w-0">
                                                        {{ application.completion_percentage }}%
                                                    </span>
                                                </div>
                                            </td>
                                            <td
                                                class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                                {{ application.created_at }}
                                            </td>
                                            <td
                                                class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                                {{ application.expires_at }}
                                            </td>
                                            <td
                                                class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                                <span :class="[(application.shared === 'Shared') ? ' bg-green-50 text-green-700 ring-green-600/10':' bg-red-50 text-red-700 ring-red-600/10','inline-flex items-center rounded-md px-1.5 py-0.5 text-xs font-medium ring-1 ring-inset']">{{ application.shared }}</span>
                                            </td>
                                            <td
                                                class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                                <inertia-link v-if="!application.is_complete"
                                                    :href="route('application.steps', application.id)"
                                                    class="rounded bg-white px-2 py-1 text-xs font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
                                                    Resume Application Process
                                                </inertia-link>
                                                <button 
                                                    v-if="application.is_complete && application.is_renters_insurance" 
                                                    type="button" 
                                                    @click.stop="showRentersInsurance(application)"
                                                    class="rounded bg-white px-2 py-1 text-xs font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
                                                    Renter's Insurance Policy
                                                </button>
                                            </td>
                                        </tr>
                                        <tr v-show="!applications.length">
                                            <td colspan="5"
                                                class="px-6 py-4 whitespace-nowrap text-dark-gray-400 text-sm font-medium text-center">
                                                No applications were found.
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>

                                <!-- Stacked layout for sm, md, and lg (table becomes vertical) -->
                                <ul role="list" class="lg:hidden grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 my-5 px-2">
                                    <li v-for="(application, index) in applications" :key="index" class="bg-white hover:outline-blue-500 hover:bg-blue-50 divide-gray-200', 'col-span-1 divide-y rounded-lg shadow-lg hover:outline hover:outline-offset-2 border">
                                        <inertia-link :href="route('application.steps', application.id)">
                                        <div class="flex flex-col p-6">
                                            <h3 class="truncate text-sm font-medium text-gray-900">{{ application.property_address }}</h3>
                                            <div class="mt-1">
                                                <div class="flex items-center justify-between mb-1">
                                                    <span class="text-sm text-gray-900 font-medium">Progress:</span>
                                                    <span v-if="application.is_complete" class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                        Complete
                                                    </span>
                                                    <span v-else class="text-xs text-gray-600 font-medium">
                                                        {{ application.steps_completed }}/{{ application.total_steps }}
                                                    </span>
                                                </div>
                                                <div v-if="!application.is_complete" class="flex items-center space-x-2">
                                                    <div class="relative group flex-1">
                                                        <div class="bg-gray-200 rounded-full h-2 cursor-pointer">
                                                            <div 
                                                                class="bg-blue-600 h-2 rounded-full transition-all duration-300 ease-in-out"
                                                                :style="{ width: application.completion_percentage + '%' }"
                                                            ></div>
                                                        </div>
                                                        <!-- Tooltip -->
                                                        <div class="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block z-20 pointer-events-none">
                                                            <div class="bg-gray-900 text-white text-xs rounded-lg py-2 px-3 whitespace-nowrap shadow-lg">
                                                                {{ application.steps_completed }}/{{ application.total_steps }} ({{ application.current_step_display_name || 'Getting Started' }})
                                                                <div class="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <span class="text-xs text-gray-600 font-medium">
                                                        {{ application.completion_percentage }}%
                                                    </span>
                                                </div>
                                            </div>
                                            <h4 class="mt-1 truncate text-sm text-gray-500"><span class="text-gray-900">Started Date:</span> {{ application.created_at }}</h4>
                                            <h4 class="mt-1 truncate text-sm text-gray-500"><span class="text-gray-900">Report Expire Date:</span> {{ application.expires_at }}</h4>
                                            <h4 class="mt-1 truncate text-sm text-gray-500"><span class="text-gray-900">Sharing Report:</span> {{ application.shared }}</h4>
                                        </div>
                                        </inertia-link>
                                        <div>
                                            <div class="divide-gray-200 -mt-px flex divide-x">
                                                <div class="flex w-0 flex-1">
                                                    <inertia-link :href="route('application.steps', application.id)" class="hover:bg-blue-200 relative -mr-px inline-flex w-0 flex-1 items-center justify-center gap-x-3 rounded-bl-lg border border-transparent py-4 text-sm font-semibold text-gray-900">
                                                        <span v-if="application.is_complete">
                                                            <font-awesome-icon icon="fa-eyes" /> View
                                                        </span>
                                                        <span v-else>
                                                            <font-awesome-icon icon="fa-edit" /> Resume Application Process
                                                        </span>
                                                    </inertia-link>
                                                    <button 
                                                        v-if="application.is_complete && application.is_renters_insurance" 
                                                        type="button" 
                                                        @click.stop="showRentersInsurance(application)"
                                                        class="hover:bg-blue-200 relative -mr-px inline-flex w-0 flex-1 items-center justify-center gap-x-3 rounded-br-lg border border-transparent py-4 text-sm font-semibold text-gray-900">
                                                        <font-awesome-icon :icon="faShieldCheck" class="h-5 w-5 text-gray-900" aria-hidden="true" /> Renter's Insurance Policy
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </li>
                                </ul>

                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <!-- 86bc1ymu6: the old "you signed up without a property link" alert rendered
                 alongside the empty state above and is simply wrong for an account created
                 for a self credit check. Its guidance now sits in that empty state. -->
        </div>

        <!-- Renter's Insurance Modal -->
        <modal-jetstream :show="showRentersInsuranceModal" :max-width="'3xl'">
            <div class="rounded-md overflow-x-auto m-2 mb-4">
                <div>
                    <div class="">
                        <div class="border-b border-gray-200">
                        <nav class="-mb-px flex space-x-8 justify-center" aria-label="Tabs">
                            <!-- Current: "border-indigo-500 text-indigo-600", Default: "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700" -->
                            <button @click="renterInsuranceActiveMenu = 1;" class="group inline-flex items-center border-b-2 border-transparent px-1 py-4 text-sm font-medium text-gray-500 hover:border-gray-300 hover:text-gray-700"  :class="renterInsuranceActiveMenu === 1 ? 'border-indigo-500 text-indigo-600' : ''">
                            <!-- Current: "text-indigo-500", Default: "text-gray-400 group-hover:text-gray-500" -->
                                <font-awesome-icon :icon="faShieldCheck" class="h-5 w-5 mr-2" aria-hidden="true" />
                                <span>Purchase Renter's Insurance Policy</span>
                            </button>
                           
                            <button @click="renterInsuranceActiveMenu = 2;" class="group inline-flex items-center border-b-2 px-1 py-4 text-sm font-medium" :class="renterInsuranceActiveMenu === 2 ? 'border-indigo-500 text-indigo-600' : ''" aria-current="page">
                                <font-awesome-icon :icon="faArrowUp" class="h-5 w-5 mr-2" aria-hidden="true" />
                                <span>Upload Renter's Insurance Policy</span>
                            </button>
                        </nav>
                        </div>
                    </div>
                </div>

                <div v-if="renterInsuranceActiveMenu === 1" class="flex flex-col">
                    <div class="overflow-x-auto">
                        <div class="align-middle inline-block min-w-full">
                            <div class="overflow-hidden bg-white ring-1 ring-gray-200 p-6 flex flex-col md:flex-row justify-between gap-6">
                                <!-- Left Section -->
                                <div class="md:w-1/2 space-y-4">
                                    <h2 class="text-xl font-semibold text-gray-800">Why Get Renters Insurance?</h2>
                                    <p class="text-gray-600">
                                        Renters insurance helps protect you and your belongings from unexpected events. It provides financial security and ensures you're covered in case of emergencies.
                                    </p>
                                    <ul class="list-disc pl-5 text-gray-600 space-y-1">
                                    <li>Protection for your personal belongings (theft, fire, water damage, etc.)</li>
                                    <li>Coverage for temporary living expenses if your rental becomes unlivable</li>
                                    <li>Personal liability coverage in case of accidents or damage to the property</li>
                                    <li>Affordable peace of mind for just a few dollars a month</li>
                                    </ul>
                                    <p class="text-gray-600">
                                        Having renters insurance means you won’t have to worry about covering major expenses on your own.
                                    </p>
                                </div>

                                <!-- Right Section -->
                                <div class="md:w-1/2 bg-gray-50 border border-gray-200 rounded-xl p-6 flex flex-col items-center text-center space-y-4">
                                    <h2 class="text-xl font-semibold text-gray-800">Get Covered in Minutes</h2>
                                    <p class="text-sm font-semibold text-gray-500 tracking-wide">POWERED BY</p>
                                    <img :src="appUrl('/sure-insurance-logo.svg')" alt="Sure Insurance Logo" class="rounded-lg bg-white ring-1 ring-gray-900/10 p-2 w-20">
                                    
                                    <p class="text-gray-600">
                                        We’ve partnered with Sure to make getting renters insurance easy and affordable. Click the link below to get started:
                                    </p>
                                    
                                    <inertia-link
                                        :href="renters_insurance_link"
                                        class="primaryButton"
                                    >
                                        Purchase Renter's Insurance Policy
                                    </inertia-link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div v-else class="flex flex-col">
                    <form @submit.prevent="store" class="space-y-4">
                        <div class="space-y-2 divide-y divide-gray-200">
                            <div class="mt-6 grid grid-cols-3 gap-y-4 gap-x-4 sm:grid-cols-6">

                                <!-- insurance_policy_files -->
                                <div class="col-span-full">
                                    <label for="insurance_policy_files" :class="form.errors.insurance_policy_files ? 'text-red-500' : 'text-gray-700'" class="block text-sm font-medium">
                                        Insurance Policy Files <span class="text-red-600">*</span>
                                    </label>
                                    <div class="mt-1">
                                        <input type="file" multiple @input="form.insurance_policy_files = $event.target.files" id="insurance_policy_files"
                                            :class="form.errors.insurance_policy_files ? 'border-red-300' : 'border-gray-300'"
                                            class="w-full shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block sm:text-sm border p-2 rounded-md">
                                            <p class="text-sm text-gray-500 mt-1">
                                                To select more than one file hold cntrl+shift
                                            </p>
                                            <div v-if="form.errors">
                                                <div v-for="(error, key, index) in form.errors" :key="key">
                                                    <p
                                                    v-if="key.startsWith('insurance_policy_files.')"
                                                    class="text-red-500 text-sm"
                                                    >
                                                    File {{ key.split('.').pop() | + 1 }}: {{ error.replace(key, '') }}
                                                    </p>
                                                </div>
                                            </div>
                                            <p v-text="form.errors.insurance_policy_files" class="text-red-500 text-sm"></p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Submit Button -->
                        <div class="text-right pr-2 flex justify-end space-x-2">
                            <button @click="showRentersInsuranceModal = false"
                                class="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 h-10">
                                Close
                            </button>
                            <form-button :form="form" type="submit" class="h-10 px-4 text-sm">
                                Save
                            </form-button>
                        </div>
                    </form>

                    <div v-if="selectedApplication.renters_insurance.length">
                        <!-- Previous Uploaded Documents Header -->
                        <div class="max-w-3xl mx-auto mt-4 mb-4 md:flex md:items-center md:justify-between md:space-x-5 lg:max-w-7xl">
                            <h2 class="text-xl font-bold">Previously Uploaded Documents</h2>
                        </div>

                        <!-- Uploaded Documents Table -->
                        <div class="flex flex-col">
                            <div class="overflow-x-auto">
                                <div class="align-middle inline-block min-w-full">
                                    <div class="shadow overflow-hidden lg:border">
                                        <table class="min-w-full divide-y divide-gray-200">
                                            <thead class="bg-blue-50">
                                                <tr>
                                                    <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                                        File
                                                    </th>
                                                    <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                                        Date Uploaded
                                                    </th>
                                                    <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left"></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <template v-for="(insurance, index) in [...selectedApplication.renters_insurance].reverse()" :key="'insurance-' + index">
                                                    <tr
                                                        v-for="(file, index2) in [...insurance.renter_insurance_files].reverse()"
                                                        :key="'file-' + index + '-' + index2"
                                                        class="bg-gray-50 border-b hover:bg-yellow-50 hover:cursor-pointer"
                                                    >
                                                        <td class="whitespace-nowrap py-4 px-4 text-sm font-medium text-gray-900">
                                                            {{ file.file_name }}
                                                        </td>
                                                        <td class="whitespace-nowrap py-4 px-4 text-sm font-medium text-gray-900">
                                                            {{ file.formatted_created_at }}
                                                        </td>
                                                        <td class="whitespace-nowrap py-4 px-4 text-sm font-medium text-gray-900 flex space-x-4">
                                                            
                                                            <a target="_blank" :href="route('renters-insurance.view-file', file.id)"
                                                                class="font-medium text-indigo-600 hover:text-indigo-500">View</a>
                                                            <a target="_blank" :href="route('renters-insurance.download-file', file.id)"
                                                                class="font-medium text-indigo-600 hover:text-indigo-500">Download</a>
                                                           
                                                        </td>
                                                    </tr>
                                                </template>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <button v-if="renterInsuranceActiveMenu === 1" @click="showRentersInsuranceModal = false" class="bg-white py-2 px-4 m-3 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 float-right">Close</button>
        </modal-jetstream>
    </tailwind-layout>
</template>

<script>
import TailwindLayout from '@/Layouts/TailwindLayout.vue'
import Button from "@/Jetstream/Button.vue";
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from "@headlessui/vue";
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faShieldCheck, faArrowUp, faExclamationTriangle } from "@fortawesome/pro-light-svg-icons";
import { faChevronDown, faChevronUp } from "@fortawesome/pro-solid-svg-icons"
import { router } from '@inertiajs/vue3';
import ModalJetstream from "@/Jetstream/Modal.vue";
import FormButton from '@/Shared/FormButton.vue';

export default {
    components: {
        Dialog,
        DialogTitle,
        DialogPanel,
        TransitionChild,
        TransitionRoot,
        Button,
        TailwindLayout,
        FontAwesomeIcon,
        ModalJetstream,
        FormButton,
    },
    props: {
        applications: Object,
        subTitle: {
            type: String,
            default: ''
        }
    },

    data() {
        return {
            showRentersInsuranceModal: false,
            renters_insurance_link: null,
            renterInsuranceActiveMenu: 1,
            selectedApplication: null,
            faShieldCheck,
            faArrowUp,
            faChevronDown,
            faChevronUp,
            faExclamationTriangle,

            form: this.$inertia.form({
                insurance_policy_files: null,
            }),
        };
    },

    methods: {
        openApplication(application_id) {
            router.visit(route('application.steps', application_id));
        },

        showRentersInsurance(application) {
            this.selectedApplication = application;
            if(application.renters_insurance_link !== null) {
                this.renters_insurance_link = application.renters_insurance_link;
                this.showRentersInsuranceModal = true;
            }
        },

        store() {
            this.form.post(this.route('renters-insurance.upload-documents', this.selectedApplication.id), {
                preserveState: true,
                onSuccess: () => {
                    location.reload();
                },
                onError: (errors) => {
                    this.form.errors = errors;
                }
            });
        },
    }
}
</script>

<style scoped>
/* Smooth transitions for progress bar */
.progress-bar {
    transition: width 0.3s ease-in-out;
}

/* Enhanced tooltip behavior */
.group:hover .group-hover\:block {
    display: block !important;
    animation: fadeIn 0.2s ease-in-out;
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(4px) translateX(-50%);
    }
    to {
        opacity: 1;
        transform: translateY(0) translateX(-50%);
    }
}

/* Better mobile tooltip handling */
@media (max-width: 1024px) {
    .group:hover .group-hover\:block {
        display: none !important;
    }
    
    /* Show tooltip on touch for mobile */
    .group:active .group-hover\:block {
        display: block !important;
    }
}
</style>
